import { BaseComponent } from '../base-component/base-component';
import { MessageType } from '../alert/global-message.interface';
import { Toast as BsToast } from 'bootstrap';
import { booleanConverter } from '../../common/converters';
import { customElement, property } from 'lit/decorators.js';
import { getMessageIcon } from '../alert/alert.util';
import { html, TemplateResult } from 'lit';
import { nothing } from 'lit-html';

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-toast': Toast;
  }
}

/**
 * A static element to display content in a toast.
 */
@customElement('mhecomm-toast')
export class Toast extends BaseComponent {
  /**
   * The icon to use in the header.
   */
  @property()
  headerIcon?: string;

  /**
   * The header of the toast.
   */
  @property()
  header?: string;

  /**
   * The type of the message.
   * @see MessageType
   */
  @property()
  type = '';

  /**
   * The body text of the toast.
   */
  @property({ type: Object })
  body?: TemplateResult;

  /**
   * Auto hide the toast.
   *
   * @default true (same as BS)
   */
  @property({ converter: booleanConverter })
  autoHide = true;

  /**
   * Delay hiding the toast (ms).
   *
   * @default 5000 (same as BS)
   */
  @property({ type: Number })
  delay = 5000;

  /**
   * Hides the header title of the toast, but still displays the header section.
   */
  @property({ type: Boolean })
  hideHeader = false;

  /**
   * Hides the close button in the header section.
   */
  @property({ type: Boolean })
  hideClose = false;

  /**
   * Shows only the body of the toast.
   */
  @property({ type: Boolean })
  showOnlyBody = false;

  /**
   * The toast Bootstrap instance.
   */
  private bsToast?: BsToast | null;

  renderComp() {
    return html`
      ${this.type !== MessageType.none
        ? html`
            <div class="position-fixed bottom-0 end-0 p-3 z-index-toast">
              <div class="toast toast-${this.type} text-start">
                ${!this.showOnlyBody
                  ? html`
                      <div class="toast-header pb-0">
                        <h2 class="d-flex fs-6 w-100 mt-2">
                          ${this.getIcon()}
                          ${!this.hideHeader ? html` <div class="me-auto fw-bold">${this.header}</div>` : nothing}
                        </h2>
                        ${!this.hideClose
                          ? html` <button
                              type="button"
                              class="btn-close m-0"
                              @click=${this.close}
                              aria-label="Close"
                            ></button>`
                          : nothing}
                      </div>
                    `
                  : nothing}
                ${this.renderBody()}
              </div>
            </div>
          `
        : nothing}
    `;
  }

  updated() {
    this.initializeBsToast();
    const toastElement = this.shadowRoot?.querySelector('.toast') as HTMLElement;
    if (toastElement) {
      toastElement.focus();
      this.addTrackedEventListener(toastElement, 'hidden.bs.toast', (): void => {
        this.fireEvent();
      });
    }
  }

  private close() {
    this.fireEvent();
    if (this.bsToast) {
      this.bsToast.hide();
    }
  }

  /**
   * Returns the icon to use in the header or body.
   */
  private getIcon() {
    const icon = this.headerIcon || getMessageIcon(this.type);
    return icon ? html` <uxt-icon class="me-2" icon="${icon}" size="lg"></uxt-icon>` : nothing;
  }

  private fireEvent() {
    const event = new CustomEvent('toast-close', { bubbles: true });
    try {
      this.dispatchEvent(event);
    } catch (exception) {
      console.error(exception);
    }
  }

  private initializeBsToast() {
    const toastElement = this.shadowRoot?.querySelector('.toast');
    if (toastElement) {
      this.bsToast = window.bootstrap?.Toast.getOrCreateInstance(toastElement, {
        autohide: this.autoHide,
        delay: this.delay,
      });
      this.bsToast?.show();
    }
  }

  /**
   * Renders the body of the toast.
   */
  private renderBody(): TemplateResult {
    return html`
      <div class="toast-body" role="alert" aria-live="assertive" aria-atomic="true" aria-relevant="all">
        ${this.showOnlyBody
          ? html`<h2 class="d-flex fs-6 gap-1 w-100 mt-2">${this.getIcon()} ${this.body}</h2>`
          : html`<span>${this.body}</span>`}
      </div>
    `;
  }
}
